/**
 * SegmentedControlHandler — 비동기 segmented-control 처리.
 *
 * admin.components.segmented-control (PHP) 가 렌더한 `.admin-segmented-control-form` 폼들을
 * FormWrapper/Form.ts 로 위임한다. 기존 page controller import 안정성을 위한 shim.
 *
 * 사용:
 *   SegmentedControlHandler.init(root);          // root 내 모든 .admin-segmented-control-form 적용
 *   SegmentedControlHandler.init(root, options); // 커스텀 콜백 포함
 *
 * 카탈로그: docs/03-constants/components.md (SegmentedControlHandler)
 * 규칙: 상태변경 작업은 Toast 패턴 — docs/01-rules/must-check.md E-2
 */

import { bindForm } from "../../core/FormWrapper";

export interface SegmentedControlHandlerOptions {
  /** 선택 성공 후 콜백. DOM 업데이트 뒤에 호출. */
  onSuccess?: (form: HTMLFormElement, result: SegmentedControlResult) => void;
  /** 오류 시 Toast.error 외에 추가 처리. */
  onError?: (form: HTMLFormElement, error: unknown) => void;
  /** true 면 오류 시 Toast.error 대신 Modal.alert (치명적 오류에 사용). */
  criticalError?: boolean;
}

export interface SegmentedControlResult {
  result: "success" | "fail";
  msg: string;
  value: string;
}

export const SegmentedControlHandler = {
  /**
   * root 내 모든 .admin-segmented-control-form 에 비동기 처리 바인딩.
   * 이미 data-sc-bound 가 있는 폼은 건너뜀.
   */
  init(root: HTMLElement | Document = document, options: SegmentedControlHandlerOptions = {}): void {
    root.querySelectorAll<HTMLFormElement>(".admin-segmented-control-form").forEach((form) => {
      form.dataset.adminFormKind = "segmented-control";
      bindForm(form);
      if (options.onSuccess) {
        form.addEventListener("admin:form:success", (event) => {
          const detail = (event as CustomEvent).detail as { normalized?: { payload?: Record<string, unknown> } };
          const payload = detail.normalized?.payload ?? {};
          options.onSuccess?.(form, {
            result: "success",
            msg: String(payload.message ?? ""),
            value: String(payload.value ?? ""),
          });
        });
      }
    });
  },

  _bindForm(form: HTMLFormElement, options: SegmentedControlHandlerOptions): void {
    form.dataset.adminFormKind = "segmented-control";
    bindForm(form);
  },

  _currentCheckedValue(form: HTMLFormElement, fallback: string): string {
    const checked = form.querySelector<HTMLInputElement>(".admin-segmented-control__input:checked");
    return checked?.value ?? fallback;
  },

  _applyState(form: HTMLFormElement, value: string): void {
    form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
      const isThis = input.value === value;
      input.checked = isThis;
      const label = form.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
      if (label) {
        label.classList.toggle("is-selected", isThis);
      }
    });
  },

  _rollback(form: HTMLFormElement, prevValue: string): void {
    this._applyState(form, prevValue);
  },

  _showError(form: HTMLFormElement, msg: string, options: SegmentedControlHandlerOptions): void {
    options.onError?.(form, new Error(msg));
  },
};
